import { colorSchema } from "app/config";
import { useTheme } from "app/shared";
import { BASE_WIDTH, TIP_WIDTH, TOTAL_HEIGHT } from "app/types/constants";

import styles from "./Funnel.module.scss";

const Funnel = ({ statistics }: { statistics: any }) => {
  const { theme } = useTheme();
  const { totalApplications, countClaimedNonZero, totalClaimed } = statistics;

  const conversionRate =
    totalApplications > 0
      ? ((countClaimedNonZero / totalApplications) * 100).toFixed(2)
      : "0.00";
  const currentTheme = colorSchema[theme] || colorSchema.light;
  const isLightTheme = theme === "light";

  const layers = [
    { label: "Referrals Joined via Your Link", value: totalApplications },
    {
      label: "Referrals Who Purchased",
      value: countClaimedNonZero,
    },
    {
      label: "Total Purchase Volume (USD)",
      value: `$${totalClaimed.toFixed(2)}`,
    },
    { label: "Purchase Rate", value: `${conversionRate}%` },
  ];

  return (
    <div
      className={`${styles.container} ${
        theme === "dark" ? styles.darkTheme : ""
      }`}
      style={{
        height: `${TOTAL_HEIGHT}px`,
      }}
    >
      <svg
        width="100%"
        height="100%"
        viewBox={`0 0 ${BASE_WIDTH} ${TOTAL_HEIGHT}`}
        preserveAspectRatio="xMidYMin meet"
      >
        {isLightTheme && (
          <defs>
            <linearGradient
              id="lightGradient1"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="0%"
            >
              <stop offset="0%" stopColor="#a8edea" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#ffa7c3" stopOpacity="0.8" />
            </linearGradient>
            <linearGradient
              id="lightGradient2"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="0%"
            >
              <stop offset="0%" stopColor="#b1f4cf" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#b490ca" stopOpacity="0.8" />
            </linearGradient>
            <linearGradient
              id="lightGradient3"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="0%"
            >
              <stop offset="0%" stopColor="#5ee7df" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#9890e3" stopOpacity="0.8" />
            </linearGradient>
            <linearGradient
              id="lightGradient4"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="0%"
            >
              <stop offset="0%" stopColor="#30cfd0" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#b87ffd" stopOpacity="0.8" />
            </linearGradient>
          </defs>
        )}
        {!isLightTheme && (
          <defs>
            <linearGradient
              id="darkGradient1"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="0%"
            >
              <stop offset="0%" stopColor="#6f45e2bc" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#88d3ceaa" stopOpacity="0.8" />
            </linearGradient>
            <linearGradient
              id="darkGradient2"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="0%"
            >
              <stop offset="0%" stopColor="#b921ffa2" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#21d5fdaf" stopOpacity="0.8" />
            </linearGradient>
            <linearGradient
              id="darkGradient3"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="0%"
            >
              <stop offset="0%" stopColor="#9a23ea8b" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#5f72bda6" stopOpacity="0.8" />
            </linearGradient>
            <linearGradient
              id="darkGradient4"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="0%"
            >
              <stop offset="0%" stopColor="#cc208da5" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#6613d2b5" stopOpacity="0.8" />
            </linearGradient>
          </defs>
        )}
        {layers.map((layer, index) => {
          const layerTopWidth =
            BASE_WIDTH - ((BASE_WIDTH - TIP_WIDTH) / layers.length) * index;
          const layerBottomWidth =
            BASE_WIDTH -
            ((BASE_WIDTH - TIP_WIDTH) / layers.length) * (index + 1);
          const layerHeight = TOTAL_HEIGHT / layers.length;

          const topLeft = {
            x: (BASE_WIDTH - layerTopWidth) / 2,
            y: index * layerHeight,
          };
          const topRight = { x: topLeft.x + layerTopWidth, y: topLeft.y };
          const bottomRight = {
            x: (BASE_WIDTH - layerBottomWidth) / 2 + layerBottomWidth,
            y: topLeft.y + layerHeight,
          };
          const bottomLeft = {
            x: (BASE_WIDTH - layerBottomWidth) / 2,
            y: topLeft.y + layerHeight,
          };
          return (
            <g key={index}>
              <polygon
                points={`
                  ${topLeft.x},${topLeft.y}
                  ${topRight.x},${topRight.y}
                  ${bottomRight.x},${bottomRight.y}
                  ${bottomLeft.x},${bottomLeft.y}
                `}
                fill={currentTheme.gradients[index]}
                stroke={currentTheme.stroke}
                strokeWidth="1"
              />
              <text
                x={BASE_WIDTH / 2}
                y={topLeft.y + layerHeight / 2}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={currentTheme.text}
                fontSize="14"
                fontWeight="700"
              >
                <tspan x={BASE_WIDTH / 2} dy="-6">
                  {layer.label}
                </tspan>
                <tspan x={BASE_WIDTH / 2} dy="18">
                  {layer.value}
                </tspan>
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

export default Funnel;
